import React from 'react';

interface Props {
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<Props> = ({ onClose }) => {
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" 
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Integritetspolicy för Utvecklaredirekt</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Privacy Policy Content */}
          <div className="prose prose-gray max-w-none">
            <div className="space-y-6 text-gray-700">
              <div>
                <p className="text-sm text-gray-500 mb-4">
                  <strong>Senast uppdaterad:</strong> 2025-08-18
                </p>
                <p className="mb-4">
                  Denna integritetspolicy beskriver hur Kapris Sweden AB (556954-3746) ("vi", "oss"), som driver landningssidan Utvecklaredirekt, samlar in och behandlar dina personuppgifter.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Vilken information samlar vi in?</h3>
                <p className="mb-3">När du använder vår tjänst, till exempel genom att fylla i vårt kontaktformulär, samlar vi in:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>E-postadress:</strong> För att kunna kontakta dig angående våra tjänster.</li>
                  <li><strong>Namn:</strong> Om du väljer att ange det.</li>
                  <li><strong>Företagsinformation:</strong> Om du anger det för att vi ska kunna ge anpassade förslag.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Hur och varför använder vi informationen?</h3>
                <p className="mb-3">Vi använder din information för att:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Kontakta dig angående våra konsulttjänster.</li>
                  <li>Skicka information om utvecklare som matchar dina behov.</li>
                  <li>Besvara dina frågor och förfrågningar.</li>
                </ul>
                <p className="mt-4">
                  Den rättsliga grunden för denna behandling är vårt berättigade intresse att kunna bedriva vår verksamhet och kommunicera med potentiella kunder. Om du ger ditt samtycke kan vi även använda din e-post för utskick.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Delar vi din information?</h3>
                <p className="mb-3">
                  <strong>Nej.</strong> Vi säljer, hyr ut eller delar aldrig din personliga information med tredje part för marknadsföringsändamål. Vi kan endast dela information om du ger oss uttryckligt samtycke eller om det krävs enligt lag.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Hur länge sparar vi informationen?</h3>
                <p>
                  Vi sparar dina uppgifter så länge de är relevanta för vår kontakt med dig. Om vi inte har haft någon aktiv dialog under två (2) år kommer dina uppgifter att raderas från våra system. Du kan när som helst begära att dina uppgifter raderas tidigare.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Dina rättigheter</h3>
                <p className="mb-3">Du har rätt att:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Begära tillgång till den information vi har om dig.</li>
                  <li>Begära att vi ändrar eller uppdaterar felaktig information.</li>
                  <li>Begära att vi raderar din information från våra system.</li>
                  <li>När som helst invända mot direktmarknadsföring.</li>
                  <li>Lämna in ett klagomål till Integritetsskyddsmyndigheten (IMY).</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Cookies</h3>
                <p>
                  Vi använder endast tekniskt nödvändiga cookies (sessionscookies) för att vår webbplats ska fungera. Dessa cookies lagrar ingen personlig information och försvinner när du stänger din webbläsare. Vi använder inga cookies för spårning, analys eller marknadsföring.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Kontakta oss</h3>
                <p className="mb-3">
                  Ansvarig för behandlingen av dina personuppgifter är Kapris Sweden AB (556954-3746). Har du frågor om denna integritetspolicy eller vill utöva dina rättigheter? Kontakta oss på:
                </p>
                <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                  <p><strong>Kapris Sweden AB</strong></p>
                  <p>Organisationsnummer: 556954-3746</p>
                  <p>Email: elin.mardfelt@utvecklaredirekt.se</p>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Utvecklaredirekt följer EU:s dataskyddsförordning (GDPR) och svensk dataskyddslagstiftning.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;
